<template>
    <div class="diagnostic-detail-container">
        <b-overlay :show="loading" rounded="sm">
            <template v-if="diagnosticDetail">
                <!-- Header con información del diagnóstico -->
                <diagnostic-header 
                    :diagnostic="diagnosticDetail.diagnostic" 
                    @back="goBack"
                    @show-documents="showDocumentsModal = true"
                />

                <!-- Pestañas -->
                <b-tabs v-model="activeTab" content-class="mt-3" justified>
                    <!-- Pestaña Diagnóstico -->
                    <b-tab title="Diagnóstico" active>
                        <template #title>
                            <i class="fa fa-stethoscope"></i> Diagnóstico
                        </template>

                        <!-- Resumen del diagnóstico -->
                        <diagnostic-summary 
                            v-if="diagnosticDetail"
                            :summary="diagnosticDetail.summary"
                            :pillars="pillarsForCalculations"
                            :total-count="totalItemsCount"
                            :rating-definitions="ratingDefinitions"
                            class="mb-4" 
                        />

                        <!-- Filtros mejorados -->
                        <diagnostic-filters-enhanced 
                            v-model="filters" 
                            :filters-data="diagnosticDetail.filters"
                            class="mb-4" 
                        />

                        <!-- Layout de 2 columnas: Lista + Panel de evaluación -->
                        <div class="diagnostic-layout">
                            <!-- Lista de ítems (izquierda) -->
                            <div class="items-list-column">
                                <items-list 
                                    :pillars="filteredPillars"
                                    :selected-item-id="selectedItem && selectedItem.id"
                                    :rating-definitions="ratingDefinitions"
                                    @item-selected="handleItemSelected"
                                />
                            </div>

                            <!-- Panel de evaluación (derecha) -->
                            <div class="evaluation-panel-column">
                                <evaluation-panel 
                                    :item="selectedItem"
                                    :pillar-name="selectedItemPillarName"
                                    :element-name="selectedItemElementName"
                                    :company-id="companyId"
                                    :rating-definitions="ratingDefinitions"
                                    :diagnostic-documents="diagnosticDetail ? diagnosticDetail.documents : []"
                                    @save-evaluation="handleSaveEvaluation"
                                    @file-uploaded="handleFileUploaded"
                                    @file-removed="handleFileRemoved"
                                />
                            </div>
                        </div>
                    </b-tab>

                    <!-- Pestaña Plan de Acción -->
                    <b-tab title="Plan de Acción" @click="loadActionPlans">
                        <template #title>
                            <i class="fa fa-calendar-check-o"></i> Plan de Acción
                        </template>
                        <action-plan-view :action-plans="actionPlans" :loading="loadingActionPlans"
                            :company-id="companyId" :workers="workers" @item-updated="loadActionPlans({ showLoader: false })" @worker-created="loadWorkers" />
                    </b-tab>

                    <!-- Pestaña Gráficas -->
                    <b-tab title="Gráficas" @click="loadGraphics" class="graphics-tab">
                        <template #title>
                            <i class="fa fa-bar-chart"></i> Gráficas
                        </template>
                        <graphics-filters
                            v-if="diagnosticDetail"
                            v-model="graphicsFilters"
                            :filters-data="diagnosticDetail.filters"
                            class="mb-3"
                        />
                        <div class="graphics-container">
                            <graphics-view 
                                :loading="loadingGraphics"
                                :chart-loading="graphicsChartLoading"
                                :table-loading="graphicsTableLoading"
                            />
                        </div>
                    </b-tab>
                </b-tabs>
            </template>

            <template v-else-if="!loading">
                <b-alert show variant="warning">
                    No se encontró el diagnóstico
                </b-alert>
            </template>
        </b-overlay>

        <!-- Modal de Documentos -->
        <diagnostic-documents-modal
            :visible="showDocumentsModal"
            :diagnostic-id="parseInt($route.params.id)"
            :company-id="companyId"
            @close="showDocumentsModal = false"
            @document-uploaded="loadDiagnostic"
        />
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import DiagnosticHeader from "./components/detail/DiagnosticHeader.vue";
import DiagnosticSummary from "./components/detail/DiagnosticSummary.vue";
import DiagnosticFiltersEnhanced from "./components/detail/DiagnosticFiltersEnhanced.vue";
import ItemsList from "./components/detail/ItemsList.vue";
import EvaluationPanel from "./components/detail/EvaluationPanel.vue";
import ActionPlanView from "./components/detail/ActionPlanView.vue";
import GraphicsView from "./components/graphics/GraphicsView.vue";
import GraphicsFilters from "./components/graphics/GraphicsFilters.vue";
import DiagnosticDocumentsModal from "./components/DiagnosticDocumentsModal.vue";

export default {
    name: "DiagnosticDetail",
    components: {
        DiagnosticHeader,
        DiagnosticSummary,
        DiagnosticFiltersEnhanced,
        ItemsList,
        EvaluationPanel,
        ActionPlanView,
        GraphicsView,
        GraphicsFilters,
        DiagnosticDocumentsModal,
    },
    data() {
        return {
            loading: false,
            loadingActionPlans: false,
            loadingGraphics: false,
            companyId: null,
            activeTab: 0,
            showDocumentsModal: false,
            graphicsFilters: {
                tourRouteIds: [],
                crossCuttingIds: [],
            },
            graphicsFiltersReloadTimeout: null,
            graphicsFiltersDirty: false,
            graphicsChartLoading: false,
            graphicsTableLoading: false,
            filtersReloadTimeout: null,
            selectedItem: null,
            selectedItemPillarName: null,
            selectedItemElementName: null,
            filters: {
                pillarId: null,
                elementId: null,
                tourRouteId: null,
                crossCuttingId: null,
                ratingId: null,
                search: "",
                applyToCalculations: false,
            },
            filtersReloadTimeout: null,
        };
    },
    computed: {
        ...mapState("skudo/diagnostics", ["diagnosticDetail", "actionPlans"]),
        ...mapState("tareas", ["empresas"]),
        ...mapState("trainings", ["workers"]),

        ratingDefinitions() {
            if (!this.diagnosticDetail || !this.diagnosticDetail.filters) {
                return [];
            }
            return this.diagnosticDetail.filters.ratings || [];
        },

        pillarsForCalculations() {
            if (!this.diagnosticDetail) return [];
            const filtered = this.diagnosticDetail.filtered_pillars;
            if (filtered && filtered.length) {
                return filtered;
            }
            return this.diagnosticDetail.pillars || [];
        },

        filteredPillars() {
            if (!this.diagnosticDetail || !this.diagnosticDetail.pillars) return [];

            let pillars = JSON.parse(JSON.stringify(this.diagnosticDetail.pillars));
            const originalLength = pillars.length;

            // Filtrar por pilar
            if (this.filters.pillarId) {
                pillars = pillars.filter(p => p.id === this.filters.pillarId);
            }

            // Filtrar por elemento
            if (this.filters.elementId) {
                pillars = pillars.map(pillar => ({
                    ...pillar,
                    elements: pillar.elements.filter(e => e.id === this.filters.elementId)
                })).filter(p => p.elements.length > 0);
            }

            // Filtrar por rating
            if (this.filters.ratingId) {
                pillars = this.filterByRating(pillars, this.filters.ratingId);
            }

            // Filtrar por ruta de visita
            if (this.filters.tourRouteId) {
                pillars = this.filterByTourRoute(pillars, this.filters.tourRouteId);
            }

            // Filtrar por criterio transversal
            if (this.filters.crossCuttingId) {
                pillars = this.filterByCrossCutting(pillars, this.filters.crossCuttingId);
            }

            // Filtrar por búsqueda
            if (this.filters.search) {
                pillars = this.filterBySearch(pillars, this.filters.search);
            }

            // Verificar si el item seleccionado está en los resultados filtrados
            if (this.selectedItem) {
                let itemFound = false;
                for (const pillar of pillars) {
                    for (const element of pillar.elements) {
                        for (const generalItem of element.general_items) {
                            if (generalItem.items.some(item => item.id === this.selectedItem.id)) {
                                itemFound = true;
                                break;
                            }
                        }
                        if (itemFound) break;
                    }
                    if (itemFound) break;
                }
                
                // Si el item seleccionado no está en los resultados, limpiarlo
                if (!itemFound) {
                    this.selectedItem = null;
                    this.selectedItemPillarName = '';
                    this.selectedItemElementName = '';
                }
            }

            return pillars;
        },
        
        totalItemsCount() {
            if (!this.pillarsForCalculations.length) return 0;

            let count = 0;
            this.pillarsForCalculations.forEach(pillar => {
                pillar.elements.forEach(element => {
                    element.general_items.forEach(generalItem => {
                        count += generalItem.items.length;
                    });
                });
            });
            return count;
        },
    },
    watch: {
        filters: {
            handler(newFilters, oldFilters) {
                const isApplying = !!(newFilters && newFilters.applyToCalculations);
                const wasApplying = !!(oldFilters && oldFilters.applyToCalculations);

                if (isApplying) {
                    this.scheduleFilteredReload();
                } else if (wasApplying && !isApplying) {
                    this.cancelFiltersReload();
                    this.loadDiagnostic({ applyFilters: false })
                        .then(() => {
                            if (this.activeTab === 2) {
                                this.loadGraphics({ showLoader: false });
                            }
                        });
                }
            },
            deep: true,
        },
        graphicsFilters: {
            handler() {
                if (!this.companyId) {
                    return;
                }

                if (this.activeTab !== 2) {
                    this.graphicsFiltersDirty = true;
                    return;
                }

                this.scheduleGraphicsFiltersReload();
            },
            deep: true,
        },
        activeTab(newValue) {
            if (newValue === 2 && this.graphicsFiltersDirty) {
                this.graphicsFiltersDirty = false;
                this.loadGraphics();
            }
        }
    },
    mounted() {
        this.loadDiagnostic();
    },
    beforeDestroy() {
        this.cancelFiltersReload();
        this.cancelGraphicsFiltersReload();
    },
    methods: {
        ...mapActions("skudo/diagnostics", [
            "getDiagnosticStructured", 
            "getActionPlansByDiagnostic", 
            "getGraphicsData",
            "updateItemResponse"
        ]),
        ...mapActions("trainings", ["fetchWorkers"]),

        async loadDiagnostic(options = {}) {
            const { applyFilters = false, showLoader = true } = options;
            const diagnosticId = this.$route.params.id;
            const companyIdFromQuery = this.$route.query.company_id || (this.empresas[0] && this.empresas[0].id);

            // Convertir a número
            this.companyId = companyIdFromQuery ? parseInt(companyIdFromQuery, 10) : null;

            if (!this.companyId) {
                this.$alertError("No se encontró la empresa");
                return;
            }

            // Cargar trabajadores para el select de responsables
            this.loadWorkers();

            if (showLoader) {
                this.loading = true;
            }
            const filters = this.buildFilterParams(applyFilters);

            try {
                await this.getDiagnosticStructured({
                    id: diagnosticId,
                    company_id: this.companyId,
                    filters,
                });
            } catch (error) {
                this.$alertError("Error al cargar el diagnóstico");
            } finally {
                if (showLoader) {
                    this.loading = false;
                }
            }
        },

        buildFilterParams(applyFilters) {
            if (!applyFilters) {
                return {};
            }

            const params = {
                apply_to_calculations: true,
            };

            if (this.filters.pillarId) params.pillar_id = this.filters.pillarId;
            if (this.filters.elementId) params.element_id = this.filters.elementId;
            if (this.filters.ratingId) params.rating_id = this.filters.ratingId;
            if (this.filters.tourRouteId) params.tour_route_id = this.filters.tourRouteId;
            if (this.filters.crossCuttingId) params.cross_cutting_id = this.filters.crossCuttingId;
            if (this.filters.search && this.filters.search.trim()) {
                params.search = this.filters.search.trim();
            }

            return params;
        },

        scheduleFilteredReload() {
            if (!this.companyId) {
                return;
            }

            this.cancelFiltersReload();
            this.filtersReloadTimeout = setTimeout(async () => {
                await this.loadDiagnostic({ applyFilters: true });
                if (this.activeTab === 2) {
                    await this.loadGraphics({ showLoader: false });
                }
            }, 400);
        },

        cancelFiltersReload() {
            if (this.filtersReloadTimeout) {
                clearTimeout(this.filtersReloadTimeout);
                this.filtersReloadTimeout = null;
            }
        },

        scheduleGraphicsFiltersReload() {
            this.cancelGraphicsFiltersReload();
            this.graphicsFiltersReloadTimeout = setTimeout(() => {
                this.loadGraphics({ showLoader: false });
            }, 300);
        },

        cancelGraphicsFiltersReload() {
            if (this.graphicsFiltersReloadTimeout) {
                clearTimeout(this.graphicsFiltersReloadTimeout);
                this.graphicsFiltersReloadTimeout = null;
            }
        },

        buildGraphicsFilterParams() {
            const params = {};

            if (this.graphicsFilters.tourRouteIds && this.graphicsFilters.tourRouteIds.length) {
                params.tour_route_ids = this.graphicsFilters.tourRouteIds;
            }

            if (this.graphicsFilters.crossCuttingIds && this.graphicsFilters.crossCuttingIds.length) {
                params.cross_cutting_ids = this.graphicsFilters.crossCuttingIds;
            }

            return params;
        },

        filterByRating(pillars, ratingId) {
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => 
                            item.response && item.response.rating && item.response.rating.id === ratingId
                        )
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },
        
        filterByTourRoute(pillars, tourRouteId) {
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => 
                            item.tour_routes && item.tour_routes.some(tr => tr.id === tourRouteId)
                        )
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },
        
        filterByCrossCutting(pillars, crossCuttingId) {
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => 
                            item.cross_cutting_criteria && item.cross_cutting_criteria.some(cc => cc.id === crossCuttingId)
                        )
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },

        filterBySearch(pillars, search) {
            const searchLower = search.toLowerCase();
            return pillars.map(pillar => ({
                ...pillar,
                elements: pillar.elements.map(element => ({
                    ...element,
                    general_items: element.general_items.map(generalItem => ({
                        ...generalItem,
                        items: generalItem.items.filter(item => {
                            const nameMatch = item.name.toLowerCase().includes(searchLower);
                            const observationsMatch = item.response && 
                                                     item.response.observations && 
                                                     item.response.observations.toLowerCase().includes(searchLower);
                            return nameMatch || observationsMatch;
                        })
                    })).filter(gi => gi.items.length > 0)
                })).filter(e => e.general_items.length > 0)
            })).filter(p => p.elements.length > 0);
        },

        handleItemSelected(item) {
            this.selectedItem = item;
            
            // Buscar el pilar y elemento del item seleccionado
            if (this.diagnosticDetail && this.diagnosticDetail.pillars) {
                for (const pillar of this.diagnosticDetail.pillars) {
                    for (const element of pillar.elements) {
                        for (const generalItem of element.general_items) {
                            if (generalItem.items.some(i => i.id === item.id)) {
                                this.selectedItemPillarName = pillar.name;
                                this.selectedItemElementName = element.name;
                                return;
                            }
                        }
                    }
                }
            }
        },
        
        async handleSaveEvaluation(evaluation, options = {}) {
            try {
                // Si el item ya tiene una respuesta, actualizar
                if (this.selectedItem.response && this.selectedItem.response.id) {
                    const response = await this.updateItemResponse({
                        id: this.selectedItem.response.id,
                        data: {
                            ...evaluation,
                            company_id: this.companyId
                        }
                    });
                    
                    // Actualizar el item seleccionado con la respuesta actualizada
                    if (response && response.data) {
                        this.selectedItem.response = {
                            ...this.selectedItem.response,
                            ...response.data
                        };
                    }
                } else {
                    // Crear nueva respuesta (endpoint pendiente)
                    this.$alertWarning('Funcionalidad de creación pendiente');
                    return;
                }
                
                // Recargar diagnóstico solo cuando el cambio de rating lo requiera
                if (options.immediate) {
                    await this.loadDiagnostic({ applyFilters: !!this.filters.applyToCalculations, showLoader: false });
                }
            } catch (error) {
                this.$alertError('Error al guardar la evaluación');
            }
        },
        
        async handleFileUploaded(attachment) {
            const currentItemId = this.selectedItem ? this.selectedItem.id : null;
            await this.loadDiagnostic({ showLoader: false });
            
            // Actualizar selectedItem con datos frescos
            if (currentItemId && this.diagnosticDetail && this.diagnosticDetail.pillars) {
                for (const pillar of this.diagnosticDetail.pillars) {
                    for (const element of pillar.elements) {
                        for (const generalItem of element.general_items) {
                            const foundItem = generalItem.items.find(i => i.id === currentItemId);
                            if (foundItem) {
                                this.selectedItem = foundItem;
                                return;
                            }
                        }
                    }
                }
            }
        },
        
        async handleFileRemoved() {
            const currentItemId = this.selectedItem ? this.selectedItem.id : null;
            await this.loadDiagnostic({ showLoader: false });
            
            // Actualizar selectedItem con datos frescos
            if (currentItemId && this.diagnosticDetail && this.diagnosticDetail.pillars) {
                for (const pillar of this.diagnosticDetail.pillars) {
                    for (const element of pillar.elements) {
                        for (const generalItem of element.general_items) {
                            const foundItem = generalItem.items.find(i => i.id === currentItemId);
                            if (foundItem) {
                                this.selectedItem = foundItem;
                                return;
                            }
                        }
                    }
                }
            }
        },

        goBack() {
            this.$router.push({
                name: "Diagnostics",
                query: { company_id: this.companyId }
            });
        },

        async loadActionPlans(options = {}) {
            // Siempre recargar para obtener datos actualizados
            const diagnosticId = this.$route.params.id;
            const { showLoader = true } = options || {};

            if (showLoader) {
                this.loadingActionPlans = true;
            }
            try {
                await this.getActionPlansByDiagnostic({
                    diagnostic_id: diagnosticId,
                    company_id: this.companyId
                });
            } catch (error) {
                this.$alertError("Error al cargar los planes de acción");
            } finally {
                if (showLoader) {
                    this.loadingActionPlans = false;
                }
            }
        },

        async loadWorkers() {
            if (!this.companyId) return;

            try {
                await this.fetchWorkers(this.companyId);
            } catch (error) {
                // Error al cargar workers
            }
        },

        async loadGraphics(options = {}) {
            const diagnosticId = this.$route.params.id;
            const { showLoader = true } = options;

            if (!this.companyId) {
                return;
            }

            if (showLoader) {
                this.loadingGraphics = true;
            }

            this.cancelGraphicsFiltersReload();
            this.graphicsChartLoading = true;
            this.graphicsTableLoading = true;

            const baseFilters = this.buildFilterParams(this.filters.applyToCalculations);
            const specificFilters = this.buildGraphicsFilterParams();
            const filters = {
                ...baseFilters,
                ...specificFilters,
            };

            try {
                await this.getGraphicsData({
                    id: diagnosticId,
                    company_id: this.companyId,
                    filters,
                });
                this.graphicsFiltersDirty = false;
            } catch (error) {
                this.$alertError("Error al cargar las gráficas");
            } finally {
                this.graphicsChartLoading = false;
                this.graphicsTableLoading = false;
                if (showLoader) {
                    this.loadingGraphics = false;
                }
            }
        },
    },
};
</script>

<style scoped>
.diagnostic-detail-container {
    width: 100%;
}

/* Layout de 2 columnas */
.diagnostic-layout {
    display: grid;
    grid-template-columns: 450px 1fr;
    gap: 24px;
    margin-top: 24px;
}

.items-list-column {
    position: sticky;
    top: 20px;
    align-self: start;
}

.evaluation-panel-column {
    min-width: 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .diagnostic-layout {
        grid-template-columns: 1fr;
        gap: 16px;
    }
    
    .items-list-column {
        position: static;
    }
}

/* Estilos para las pestañas */
.custom-tabs>>>.nav-tabs {
    border-bottom: 2px solid #dee2e6;
}

.custom-tabs>>>.nav-link {
    color: #6c757d;
    font-weight: 500;
    border: none;
    border-bottom: 3px solid transparent;
    transition: all 0.3s ease;
}

.custom-tabs>>>.nav-link:hover {
    color: #495057;
    border-bottom-color: #dee2e6;
}

.custom-tabs>>>.nav-link.active {
    color: #23a8d9;
    border-bottom-color: #23a8d9;
    background-color: transparent;
}

.custom-tabs>>>.tab-content {
    padding-top: 20px;
}

/* Quitar borde del contenedor de tabs */
.diagnostic-detail-container>>>.tab-content {
    border: none !important;
    padding: 0 !important;
    background: transparent !important;
}

/* Reducir padding del contenido de los tabs */
.diagnostic-detail-container>>>.tab-pane {
    padding: 0 !important;
}

/* Estilos para el contenedor de gráficas */
.graphics-container {
    background-color: transparent;
    padding: 0;
    min-height: auto;
}
</style>
